import dynamic from 'next/dynamic';
import styled from 'styled-components';

import { FeatureFlags } from 'featureFlags/constants';
import { LatestSearches } from 'search/components/search/LatestSearches';
import { SearchResultImageGrid } from 'search/components/search/SearchResultImageGrid';
import { ICollection } from 'search/types/Collection';
import { getNameAlignment } from 'search/utils/getNameAlignment';
import { Loader } from 'shared/components/Loader';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import type { IConstructorResult } from 'shared/types/ConstructorResult';
import { getImageType } from 'shared/utils/gridEventTrackingUtils';
import { navigationThemes } from 'styles/theme/navigation';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';

const TP = 'search.components.search.SearchResult';

const SearchTermSearchSuggestions = dynamic(() => import('./SearchTermSearchSuggestions'));

interface ISearchResults {
  collectionResults?: Array<ICollection>;
  error: Error;
  isLoading?: boolean;
  resultId: string;
  productResults: Array<IConstructorResult>;
  searchTerm: string;
}

const SearchResults = ({
  collectionResults,
  error,
  isLoading,
  resultId,
  productResults,
  searchTerm,
}: ISearchResults) => {
  const isLatestSearchesEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_LATEST_SEARCHES);
  const ConstructorClient = useConstructorClient();
  const eventTrackingEventContext = useEventContext();
  const { user } = useStoreSelector((state) => state.auth);

  const handleProductClick = (result, index: number) => {
    const { data, value } = result;

    sendTrackingEvent('CONSTRUCTOR_GRID_CELL_TAP', {
      event_context: eventTrackingEventContext,
      is_filtered: false,
      is_preview: true,
      object_id: data.id,
      pdp_image_type: getImageType(data.image_url, data?.cheapest_product),
      position: (index + 1).toString(),
      product_count: data.count_for_product_condition || '1',
      product_template_slug: data.slug,
      result_id: resultId,
      search_string: searchTerm,
      ...(user && { userID: user.id }),
    });

    ConstructorClient?.tracker?.trackAutocompleteSelect(value, {
      original_query: searchTerm,
      section: 'Products',
    });
  };

  const handleCollectionClick = (index: number) => {
    if (collectionResults.length > 0) {
      const collection = collectionResults.at(index);

      ConstructorClient?.tracker?.trackAutocompleteSelect(collection.value, {
        original_query: searchTerm,
        section: 'Collections',
      });
    }
  };

  if (error) {
    return null;
  }

  if (isLoading) {
    return (
      <SearchResults.Wrapper>
        <Loader />
      </SearchResults.Wrapper>
    );
  }

  if (productResults.length === 0) {
    return (
      <SearchResults.NoResultsWrapper>
        <SearchResults.NoResults data-qa="no_search_results">
          {t(`${TP}.noResult`, 'No results were found for {searchTerm}', {
            searchTerm,
          })}
        </SearchResults.NoResults>
        {isLatestSearchesEnabled && <LatestSearches />}
      </SearchResults.NoResultsWrapper>
    );
  }

  return (
    <SearchResults.Wrapper>
      <SearchTermSearchSuggestions
        collectionResults={collectionResults}
        searchTerm={searchTerm}
        handleOnClick={handleCollectionClick}
      />
      <SearchResults.GridWrapper data-qa="search_results_grid">
        {productResults.map((result, index) => {
          return (
            <SearchResultImageGrid
              align={getNameAlignment(index, 5)}
              imageSize="20vw"
              key={result.data.id}
              onClick={() => handleProductClick(result, index)}
              product={result as { data: Record<string, any>; value: string }}
              missingAssetPath={navigationThemes.light.missingAssetPath}
              showPtNames
            />
          );
        })}
      </SearchResults.GridWrapper>
    </SearchResults.Wrapper>
  );
};

SearchResults.Wrapper = styled.div`
  padding: 20px;
`;

SearchResults.GridWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 5px;
`;

SearchResults.LoaderWrapper = styled.div`
  margin: 16px;
`;

SearchResults.NoResultsWrapper = styled.div`
  padding: 16px;
`;

SearchResults.NoResults = styled.div`
  margin-bottom: 24px;
`;

export default SearchResults;
